import { when } from '@execonline-inc/maybe-adapter';
import { SessionData } from '@execonline-inc/unified-auth.private';
import { always, pipe } from '@kofno/piper';
import { just } from 'maybeasy';
import { currentUserStore } from '../CurrentUserStore';
import { sessionStore } from './Store';

export const discardSession = () => sessionStore.absent();

// The server no longer has a session for this browser: a request was refused and renewing the
// app session was refused too (signed out in another ExecOnline app, or the session reached its
// time limit). Show the login page with that reason rather than leaving each page to render its
// own load error (DVLP-8082).
export const endExpiredSession = (): void => {
  currentUserStore.setAnonymous(just('Your session has expired, please log in again.'));
  discardSession();
};

// Called with the status of a refused session renewal. The session endpoint answers 401 when the
// user session is gone and 404 when it has no app session to give this browser
// (UnifiedSession::ApplicationSessionsControllerSupport#show); either way the server session is
// over. Any other status leaves the original request's error standing.
export const endSessionIfRenewalRefused = (status: number): void => {
  if (status === 401 || status === 404) {
    endExpiredSession();
  }
};

export const establishSession = pipe(sessionStore.present, currentUserStore.setIdentifying);

export const handleRecovery =
  (onMatchingUser: (sessionData: SessionData) => void) =>
  (sessionData: SessionData): void =>
    just({})
      .assign('oldUserId', always(sessionStore.userId))
      .assign('newUserId', always(sessionData.userId))
      .andThen(({ oldUserId, newUserId }) => when(oldUserId === newUserId, sessionData))
      .cata({ Just: onMatchingUser, Nothing: () => window.location.reload() });
